<template>
  <div class="container py-5">
    <div id="editor"></div>
    <loading-wrap :loading="loading">
      <h4>{{ id ? "Edit" : "Add" }} Template</h4>
      <div class="alert alert-danger" v-if="error">{{ error }}</div>
      <div class="row justify-content-center" v-show="page === 1">
        <div class="col-12">
          <div class="row">
            <div class="col-4">
              <div class="form-group">
                <label>Template Name</label>
                <input
                  type="text"
                  class="form-control"
                  placeholder="Template Name"
                  v-model="form.name"
                  required
                />
              </div>
            </div>
            <div class="col-4">
              <div class="form-group">
                <label>
                  Year Level
                  <help-tip title="Prep = 0" />
                </label>
                <input
                  type="number"
                  class="form-control"
                  min="0"
                  step="1"
                  max="7"
                  placeholder="Year Level"
                  required
                  v-model="form.yearLevel"
                />
              </div>
            </div>
            <div class="col-4">
              <div class="form-group">
                <label>Subject</label>
                <v-select
                  v-model="form.subject"
                  :clearable="false"
                  :options="['english', 'maths']"
                  label="name"
                >
                  <template slot="selected-option" slot-scope="option">
                    <span style="text-transform: capitalize">{{
                      option.name
                    }}</span>
                  </template>
                  <template slot="option" slot-scope="option">
                    <span style="text-transform: capitalize">{{
                      option.name
                    }}</span>
                  </template>
                </v-select>
              </div>
            </div>
          </div>
          <hr />
          <div class="d-flex mb-2 justify-content-between align-items-center">
            <h6>Questions</h6>
            <button
              type="button"
              @click="addQuestion()"
              class="btn btn-primary"
            >
              <span>Add Question</span>
            </button>
          </div>
          <template v-if="form.questions.length">
            <draggable
              :list="form.questions"
              @start="dragging = true"
              @end="dragging = false"
            >
              <question-card
                @deletes="deleteS"
                v-for="(q, index) in form.questions"
                v-model="form.questions[index]"
                :categorys="categorys"
                :order="index"
                :key="index"
            /></draggable>
          </template>
          <no-results v-else />
          <hr />
          <div class="text-right">
            <button
              type="button"
              @click="changePage(1)"
              class="btn btn-success"
            >
              <span>Next</span>
            </button>
          </div>
        </div>
      </div>
      <div v-show="page === 2">
        <h6>Pronoun Variables</h6>
        <div class="mb-3">
          <span
            class="badge badge-secondary mr-1"
            style="font-size: 14px"
            v-for="variable in pronounVariables"
            :key="variable"
            draggable
            @drag="() => (drag = `[${variable}]`)"
            >[{{ variable }}]</span
          >
        </div>
        <h6>Accessory Variables</h6>
        <div class="mb-3">
          <span
            class="badge badge-secondary mr-1"
            style="font-size: 14px"
            v-for="variable in accessoryVariables"
            :key="variable"
            draggable
            @drag="() => (drag = `[${variable}]`)"
            >[{{ variable }}]</span
          >
        </div>
        <h6>
          Category Variables
          <help-tip
            title="Start the variable with IF: and then from there you can use regular JavaScript logic and operators. E.g. [IF: ((Trigonometry) / 2) <= 4] DISPLAY THIS CONTENT [/]"
          />
        </h6>
        <div class="mb-3">
          <span
            class="badge badge-secondary mr-1"
            style="font-size: 14px"
            v-for="variable in categoryVariables"
            :key="variable"
            draggable
            @drag="() => (drag = `[IF: (${variable})] [/]`)"
            >[IF: ({{ variable }})] [/]</span
          >
        </div>
        <h6>Question Variables</h6>
        <div class="mb-3">
          <span
            class="badge badge-secondary mr-1"
            style="font-size: 14px"
            v-for="(q, i) in form.questions"
            :key="i"
            draggable
            @drag="() => (drag = `[IF: (Q${i + 1})] [/]`)"
            >[IF: (Q{{ i + 1 }})] [/]</span
          >
        </div>
        <h6>Additional Variables</h6>
        <div class="mb-3">
          <span
            class="badge badge-secondary mr-1"
            style="font-size: 14px"
            v-for="location in ['Cannon Hill', 'In-Home', 'Paddington']"
            draggable
            @drag="() => (drag = `[IF: (LOCATION) === '${location}'] [/]`)"
            >[IF: (LOCATION) === '{{ location }}'] [/]</span
          >
        </div>
        <h6>
          Response Content
          <help-tip
            title="Content of particular categories, displayed as a bullet-point list and ordered by entered-order."
          />
        </h6>
        <div class="mb-3">
          <span
            class="badge badge-secondary mr-1"
            style="font-size: 14px"
            v-for="variable in categoryVariables"
            :key="variable"
            draggable
            @drag="() => (drag = `[CONTENT: ${variable}]`)"
            >[CONTENT: {{ variable }}]</span
          >
        </div>
        <hr />
        <div @drop="() => (form.assessmentText += drag)" @dragover.prevent>
          <quill-editor v-model="form.assessmentText" />
        </div>

        <hr />
        <div class="d-flex justify-content-between">
          <button
            type="button"
            @click="changePage(-1)"
            class="btn btn-secondary"
          >
            <span>Back</span>
          </button>
          <button
            type="button"
            @click="submit()"
            :disabled="formLoading"
            class="btn btn-success"
          >
            <svg-loader />
            <span>Submit</span>
          </button>
        </div>
      </div>
    </loading-wrap>
  </div>
</template>

<script>
import Quill from "quill";
import draggable from "vuedraggable";
export default {
  name: "handle",
  display: "Handle",
  instruction: "Drag using the handle icon",
  components: {
    draggable,
  },
  data() {
    return {
      drag: "",
      unfilteredCategorys: [],
      pronounVariables: [
        "He/She",
        "Her/Him",
        "Her/His",
        "he/she",
        "her/him",
        "her/his",
      ],
      accessoryVariables: [
        "YEAR LEVEL",
        "SUBJECT",
        "FIRST NAME",
        "LAST NAME",
        "TUTOR FIRST NAME",
        "TUTOR LAST NAME",
        "TEST DATE",
        "LOCATION",
      ],
      page: 1,
      loadsRequired: 0,
      form: {
        name: "",
        yearLevel: null,
        subject: "",
        questions: [],
        assessmentText: "",
      },
      loading: true,
      formLoading: false,
      id: null,
      error: null,
    };
  },
  created() {
    const { id } = this.$route.query;
    if (id) {
      this.id = id;

      this.axios
        .get(`${process.env.VUE_APP_API_URL}/api/categorys`)
        .then((res) => {
          this.unfilteredCategorys = res.data;
        });

      this.axios
        .get(`${process.env.VUE_APP_API_URL}/api/templates/${id}`)
        .then((res) => {
          this.form = res.data;
          this.loading = false;
        });
    } else {
      this.axios
        .get(`${process.env.VUE_APP_API_URL}/api/categorys`)
        .then((res) => {
          this.unfilteredCategorys = res.data;
          this.loading = false;
          this.addQuestion();
        });
    }
  },
  computed: {
    categoryVariables() {
      return [
        ...new Set(
          this.form.questions.map(({ category }) => {
            if (category) {
              return category;
            }
          })
        ),
      ].filter((i) => !!i);
    },
    categorys() {
      const { subject } = this.form;
      if (subject === "") {
        return this.unfilteredCategorys;
      }

      return this.unfilteredCategorys.filter((c) => c && c.subject === subject);
    },
  },
  methods: {
    deleteS(index) {
      this.form.questions = [
        ...this.form.questions.slice(0, index),
        ...this.form.questions.slice(index + 1, this.form.questions.length),
      ];
    },
    changePage(change = 1) {
      this.page += change;
    },
    addQuestion() {
      this.form.questions.push({
        category: "",
        question: "",
        file: null,
        filename: "",
        newfile: false,
        tutorSentences: [
          {
            number: 0,
            sentence: "",
          },
          {
            number: 1,
            sentence: "",
          },
          {
            number: 2,
            sentence: "",
          },
          {
            number: 3,
            sentence: "",
          },
          {
            number: 4,
            sentence: "",
          },
          {
            number: 5,
            sentence: "",
          },
        ],
        assessmentSentences: [
          {
            number: 0,
            sentence: "",
          },
          {
            number: 1,
            sentence: "",
          },
          {
            number: 2,
            sentence: "",
          },
          {
            number: 3,
            sentence: "",
          },
          {
            number: 4,
            sentence: "",
          },
          {
            number: 5,
            sentence: "",
          },
        ],
      });
    },
    sendOff() {
      const mode = this.form._id ? "patch" : "post";
      const URL = this.form._id
        ? `${process.env.VUE_APP_API_URL}/api/templates/${this.form._id}`
        : `${process.env.VUE_APP_API_URL}/api/templates`;

      const div = document.createElement("div");
      div.innerHTML = this.form.assessmentText;
      this.form.plainText = div.innerText;

      // Editing and creating
      this.axios[mode](URL, this.form)
        .then((res) => {
          const message =
            mode === "patch"
              ? "Template successfully edited."
              : "New template successfully added.";

          this.$root.$emit("popup", message);
          this.$router.push("/dashboard/templates");
        })
        .catch((res) => {
          this.error =
            "Interal server error. You may have forgotten to fill out a required field.";
        })
        .finally(() => (this.formLoading = false));
    },
    submit() {
      this.formLoading = true;
      this.form.questions = this.form.questions.map((q, i) => ({
        ...q,
        order: i,
      }));

      const config = {
        headers: {
          "Content-Type": undefined,
        },
      };

      this.loadsRequired = this.form.questions.filter((q) => q.newfile).length;

      if (this.loadsRequired === 0) {
        return this.sendOff();
      }

      this.form.questions.forEach((q, i) => {
        if (q.newfile) {
          this.axios
            .post(
              `${process.env.VUE_APP_API_URL}/api/templates/uploads`,
              q.file,
              config
            )
            .then((res) => {
              console.log(res);
              this.form.questions[i].filename = res.data;
              this.loadsRequired--;

              if (this.loadsRequired === 0) {
                this.sendOff();
              }
            })
            .catch((res) => console.log(res));
        }
      });
    },
  },
};
</script>

<style scoped>
.handle {
  float: left;
  padding-top: 8px;
  padding-bottom: 8px;
}
</style>
